/**
 * The event vocabulary for the signing journey. Every log that represents a
 * step in the customer journey carries one of these as `event`, so a single
 * customer can be followed end to end with `@flow_id:<id>` in Datadog.
 *
 * Event names are stable identifiers used by dashboards and monitors - renaming
 * one breaks those, so add rather than repurpose.
 */
export const LogEvent = {
	AuthLoginStarted: "auth.login.started",
	AuthLoginDuplicateSuppressed: "auth.login.duplicate-suppressed",
	AuthAuthorizeRedirect: "auth.authorize.redirect",
	AuthCallbackReceived: "auth.callback.received",
	AuthCallbackFailed: "auth.callback.failed",
	AuthHandlerFailed: "auth.handler.failed",
	AuthLoginFailed: "auth.login.failed",
	AuthLoginSucceeded: "auth.login.succeeded",
	AuthTokenRefreshFailed: "auth.token.refresh.failed",
	AuthTokenRefreshed: "auth.token.refreshed",
	AuthRefreshTokenMissing: "auth.token.refresh-token-missing",
	AuthLogout: "auth.logout",
	AuthHostRedirected: "auth.host.redirected",
	AuthHostMismatch: "auth.host.mismatch",
	AuthWrongUser: "auth.wrong-user",
	AuthSessionMissing: "auth.session.missing",
	FlowTaskReceived: "flow.task.received",
	FlowTaskUnresolvable: "flow.task.unresolvable",
	FlowResolveFailed: "flow.resolve.failed",
	FlowStaleTerminalSignal: "flow.stale-terminal-signal",
	FlowHasErrorsIgnored: "flow.has-errors.ignored",
	FlowContextInvalid: "flow.context.invalid",
	FlowOfferInconsistent: "flow.offer.inconsistent",

	StepViewed: "step.viewed",
	FormValidationFailed: "form.validation.failed",
	FormPurposeMissing: "form.purpose.missing",
	RefinanceSubmitBlocked: "refinance.submit.blocked",

	TaskSubmitSucceeded: "task.submit.succeeded",
	TaskSubmitRejected: "task.submit.rejected",
	TaskSubmitFailed: "task.submit.failed",
	TaskSubmitSkipped: "task.submit.skipped",
	TaskSubmitAbandoned: "task.submit.abandoned",

	UpstreamFlowRequest: "upstream.flow.request",
	UpstreamCmsRequest: "upstream.cms.request",

	CmsContentInvalid: "cms.content.invalid",
	CmsAlertOverlap: "cms.alert.overlap",
	CmsFlagDuplicate: "cms.flag.duplicate",
	CmsOptionListDuplicate: "cms.option-list.duplicate",
	ConstantOptionsMissing: "constants.options.missing",

	PollStarted: "poll.started",
	PollResolved: "poll.resolved",
	PollSessionLost: "poll.session-lost",
	PollNetwork: "poll.network",
	PollTimeout: "poll.timeout",
	PollUnavailable: "poll.unavailable",
	PollUnavailableTimeout: "poll.unavailable-timeout",

	SignRedirect: "sign.redirect",
	SignRedirectSuppressed: "sign.redirect.suppressed",
	SignCallbackFailed: "sign.callback.failed",
	TinkCallbackFailed: "tink.callback.failed",
	TinkCallbackReauth: "tink.callback.reauth",
	TinkAccountInvalid: "tink.account.invalid",

	ElektraCardDetailsFailed: "elektra.card-details.failed",

	TerminalReached: "terminal.reached",

	ErrorBoundaryTriggered: "error.boundary.triggered",
	ConfigMissing: "config.missing",
} as const;

export type LogEvent = (typeof LogEvent)[keyof typeof LogEvent];

export type LogOutcome = "success" | "failure";

export type LogLevel = "info" | "warn" | "error";

/** Present when a log fires before a flow id can be recovered. */
export const UNKNOWN_FLOW_ID = "unknown";

export const flowIdForLog = (flowId?: string | null): string => {
	const trimmed = flowId?.trim();
	return trimmed ? trimmed : UNKNOWN_FLOW_ID;
};

/**
 * The fields every log shares. They are serialised as top-level attributes so
 * Datadog exposes them as facets - anything nested under `context` cannot be
 * filtered or grouped on without extra setup.
 */
export interface LogFields {
	event?: LogEvent;
	task?: string;
	step?: string;
	flowId: string;
	referenceId?: string;
	agentId?: string;
	processType?: string;
	country?: string;
	statusCode?: number;
	/** How long the customer-visible wait took, for a measure in Datadog. */
	durationMs?: number;
	outcome?: LogOutcome;
}
